import { isObjectRecord } from 'Utils/typeGuards';

import type { ServerModel } from '../types';

export { isObjectRecord };

/**
 * `value is T[]` when `value` is an array AND every element passes
 * `predicate`. Empty arrays trivially pass — mirrors `Array.prototype.every`.
 */
export function isArrayOf<T>(value: unknown, predicate: (item: unknown) => item is T): value is T[] {
  return Array.isArray(value) && value.every(predicate);
}

/**
 * Optional-array variant: passes for `undefined`/`null` and for arrays
 * where every element matches `predicate`. Use for response fields the
 * server may legitimately omit (e.g. `Facets[].Values`, `Facets[].Selection`).
 */
export function isOptionalArrayOf<T>(
  value: unknown,
  predicate: (item: unknown) => item is T,
): value is T[] | null | undefined {
  return value === undefined || value === null || isArrayOf(value, predicate);
}

export function isString(value: unknown): value is string {
  return typeof value === 'string';
}

export function isNumber(value: unknown): value is number {
  return typeof value === 'number';
}

export function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

/** `Record<string, string>` — `extra`-style flat string maps. */
export function isStringRecord(value: unknown): value is Record<string, string> {
  if (!isObjectRecord(value)) {
    return false;
  }
  for (const key in value) {
    if (typeof value[key] !== 'string') {
      return false;
    }
  }
  return true;
}

export function hasStringField<K extends string>(
  value: Record<string, unknown>,
  key: K,
): value is Record<K, string> & Record<string, unknown> {
  return typeof value[key] === 'string';
}

export function hasNumberField<K extends string>(
  value: Record<string, unknown>,
  key: K,
): value is Record<K, number> & Record<string, unknown> {
  return typeof value[key] === 'number';
}

export function hasBooleanField<K extends string>(
  value: Record<string, unknown>,
  key: K,
): value is Record<K, boolean> & Record<string, unknown> {
  return typeof value[key] === 'boolean';
}

export function hasOptionalStringField<K extends string>(
  value: Record<string, unknown>,
  key: K,
): value is Partial<Record<K, string>> & Record<string, unknown> {
  return value[key] === undefined || typeof value[key] === 'string';
}

export function hasOptionalNumberField<K extends string>(
  value: Record<string, unknown>,
  key: K,
): value is Partial<Record<K, number>> & Record<string, unknown> {
  return value[key] === undefined || typeof value[key] === 'number';
}

export function hasOptionalNumericField<K extends string>(
  value: Record<string, unknown>,
  key: K,
): value is Partial<Record<K, number | string>> & Record<string, unknown> {
  return value[key] === undefined || typeof value[key] === 'number' || typeof value[key] === 'string';
}

export function hasOptionalBooleanField<K extends string>(
  value: Record<string, unknown>,
  key: K,
): value is Partial<Record<K, boolean>> & Record<string, unknown> {
  return value[key] === undefined || typeof value[key] === 'boolean';
}

export function hasOptionalLiteralField<K extends string, L extends string>(
  value: Record<string, unknown>,
  key: K,
  literals: readonly L[],
): value is Partial<Record<K, L>> & Record<string, unknown> {
  const v = value[key];
  return v === undefined || (typeof v === 'string' && (literals as readonly string[]).includes(v));
}

export function isGenericItem(value: unknown): value is ServerModel.GenericItem {
  return isObjectRecord(value);
}

/**
 * Validate a single facet value (`Values[i]` / `Selection[i]`). Every
 * field on `ServerModel.Facet` is optional, so this only enforces type
 * constraints when the field is present.
 */
export function isFacetValueShape(value: unknown): value is ServerModel.Facet {
  return (
    isObjectRecord(value) &&
    hasOptionalStringField(value, 'Value') &&
    hasOptionalNumberField(value, 'HitCount') &&
    hasOptionalStringField(value, 'Term') &&
    hasOptionalBooleanField(value, 'Selected') &&
    hasOptionalStringField(value, 'Payload') &&
    hasOptionalStringField(value, 'Url') &&
    hasOptionalStringField(value, 'ImageUrl') &&
    hasOptionalNumberField(value, 'TreeLevel')
  );
}

/**
 * Strict `FacetCategory` predicate: validates `FieldName` and optional
 * `DisplayName`, then recursively checks `Values` / `Selection` (when present). Does
 * not constrain the optional `DisplayMode` literal — those values come
 * from the server's enum and we let downstream UI tolerate unknowns.
 */
export function isFacetCategoryShape(value: unknown): value is ServerModel.FacetCategory {
  return (
    isObjectRecord(value) &&
    hasStringField(value, 'FieldName') &&
    hasOptionalStringField(value, 'DisplayName') &&
    isOptionalArrayOf(value.Values, isFacetValueShape) &&
    isOptionalArrayOf(value.Selection, isFacetValueShape)
  );
}

/** `State` is optional because existing search responses omit it on spellcheck corrections. */
export function isCorrectionShape(value: unknown): value is ServerModel.Correction {
  return (
    isObjectRecord(value) &&
    hasOptionalStringField(value, 'State') &&
    hasStringField(value, 'Original') &&
    hasStringField(value, 'Replace') &&
    hasBooleanField(value, 'Apply')
  );
}

/**
 * Discriminated-union predicate for `Action` — narrows on `Type` and
 * verifies the variant-specific payload string. Unknown `Type` values
 * fail the guard so a rogue server addition is logged via the boundary
 * predicate caller rather than crashing the reducer.
 */
export function isActionShape(value: unknown): value is ServerModel.Action {
  if (!isObjectRecord(value) || typeof value.Type !== 'string') {
    return false;
  }
  switch (value.Type) {
    case 'None':
      return true;
    case 'Redirect':
      return hasStringField(value, 'RedirectUrl');
    case 'ReplaceQuery':
      return hasStringField(value, 'QueryReplacement');
    case 'AddSuggestion':
      return hasStringField(value, 'SuggestionText') && hasStringField(value, 'SuggestionImage');
    default:
      return false;
  }
}

const autocompleteVariantTypes = ['PopularSearch', 'Freetext', 'Product', 'Category'] as const;

/**
 * Discriminated-union predicate for `AutocompleteItem` — narrows on
 * `Type` and verifies the variant-required fields (e.g. `Text`,
 * `HitCount`). Unknown variants are rejected so the upstream filter in
 * `getAutocompleteResponse` can drop and log them instead of pushing
 * malformed items into rendering code.
 */
export function isAutocompleteItemShape(value: unknown): value is ServerModel.AutocompleteItem {
  if (!isObjectRecord(value) || typeof value.Type !== 'string') {
    return false;
  }
  switch (value.Type) {
    case 'PopularSearch':
      return (
        hasStringField(value, 'Text') &&
        hasStringField(value, 'TextHighlight') &&
        hasOptionalNumberField(value, 'SessionCount')
      );
    case 'Freetext':
      return (
        hasStringField(value, 'Text') &&
        hasStringField(value, 'TextHighlight') &&
        hasOptionalNumberField(value, 'HitCount')
      );
    case 'Product':
      return hasStringField(value, 'SuggestionGroup') && hasNumberField(value, 'TotalHits');
    case 'Category':
      return (
        hasStringField(value, 'Value') &&
        hasStringField(value, 'FacetValue') &&
        hasStringField(value, 'SuggestionGroup') &&
        hasOptionalNumericField(value, 'HitCount') &&
        (hasStringField(value, 'FieldName') || hasStringField(value, 'RedirectUrl'))
      );
    default:
      return false;
  }
}

/** Whitelist used by the boundary check above; exported for test fixtures. */
export const knownAutocompleteVariantTypes = autocompleteVariantTypes;

type ValidationError = string | undefined;

function getActualType(value: unknown): string {
  if (value === undefined) {
    return 'missing';
  }
  if (value === null) {
    return 'null';
  }
  if (Array.isArray(value)) {
    return 'array';
  }
  return typeof value;
}

function validationError(path: string, expected: string, actual: unknown): string {
  return `${path}: expected ${expected}, got ${getActualType(actual)}`;
}

function validateObject(value: unknown, path: string): ValidationError {
  return isObjectRecord(value) ? undefined : validationError(path, 'object', value);
}

function validateField(
  value: Record<string, unknown>,
  path: string,
  key: string,
  expected: string,
  predicate: (field: unknown) => boolean,
): ValidationError {
  const field = value[key];
  const fieldPath = path === '' ? key : `${path}.${key}`;
  return predicate(field) ? undefined : validationError(fieldPath, expected, field);
}

function validateOptionalField(
  value: Record<string, unknown>,
  path: string,
  key: string,
  expected: string,
  predicate: (field: unknown) => boolean,
): ValidationError {
  return value[key] === undefined ? undefined : validateField(value, path, key, expected, predicate);
}

function validateArray(
  value: unknown,
  path: string,
  itemValidator: (item: unknown, itemPath: string) => ValidationError,
): ValidationError {
  if (!Array.isArray(value)) {
    return validationError(path, 'array', value);
  }

  for (let i = 0; i < value.length; i += 1) {
    const itemError = itemValidator(value[i], `${path}[${i}]`);
    if (itemError !== undefined) {
      return itemError;
    }
  }

  return undefined;
}

function validateOptionalArray(
  value: unknown,
  path: string,
  itemValidator: (item: unknown, itemPath: string) => ValidationError,
): ValidationError {
  return value === undefined || value === null ? undefined : validateArray(value, path, itemValidator);
}

function validateGenericItem(value: unknown, path: string): ValidationError {
  return validateObject(value, path);
}

function validateFacetValue(value: unknown, path: string): ValidationError {
  const objectError = validateObject(value, path);
  if (objectError !== undefined) {
    return objectError;
  }

  const facet = value as Record<string, unknown>;
  return (
    validateOptionalField(facet, path, 'Value', 'string when present', isString) ??
    validateOptionalField(facet, path, 'HitCount', 'number when present', isNumber) ??
    validateOptionalField(facet, path, 'Term', 'string when present', isString) ??
    validateOptionalField(facet, path, 'Selected', 'boolean when present', isBoolean) ??
    validateOptionalField(facet, path, 'Payload', 'string when present', isString) ??
    validateOptionalField(facet, path, 'Url', 'string when present', isString) ??
    validateOptionalField(facet, path, 'ImageUrl', 'string when present', isString) ??
    validateOptionalField(facet, path, 'TreeLevel', 'number when present', isNumber)
  );
}

function validateFacetCategory(value: unknown, path: string): ValidationError {
  const objectError = validateObject(value, path);
  if (objectError !== undefined) {
    return objectError;
  }

  const facet = value as Record<string, unknown>;
  return (
    validateField(facet, path, 'FieldName', 'string', isString) ??
    validateOptionalField(facet, path, 'DisplayName', 'string when present', isString) ??
    validateOptionalArray(facet.Values, `${path}.Values`, validateFacetValue) ??
    validateOptionalArray(facet.Selection, `${path}.Selection`, validateFacetValue)
  );
}

function validateCorrection(value: unknown, path: string): ValidationError {
  const objectError = validateObject(value, path);
  if (objectError !== undefined) {
    return objectError;
  }

  const correction = value as Record<string, unknown>;
  return (
    validateOptionalField(correction, path, 'State', 'string when present', isString) ??
    validateField(correction, path, 'Original', 'string', isString) ??
    validateField(correction, path, 'Replace', 'string', isString) ??
    validateField(correction, path, 'Apply', 'boolean', isBoolean)
  );
}

function validateAction(value: unknown, path: string): ValidationError {
  const objectError = validateObject(value, path);
  if (objectError !== undefined) {
    return objectError;
  }

  const action = value as Record<string, unknown>;
  const typeError = validateField(action, path, 'Type', 'string', isString);
  if (typeError !== undefined) {
    return typeError;
  }

  switch (action.Type) {
    case 'None':
      return undefined;
    case 'Redirect':
      return validateField(action, path, 'RedirectUrl', 'string', isString);
    case 'ReplaceQuery':
      return validateField(action, path, 'QueryReplacement', 'string', isString);
    case 'AddSuggestion':
      return (
        validateField(action, path, 'SuggestionText', 'string', isString) ??
        validateField(action, path, 'SuggestionImage', 'string', isString)
      );
    default:
      return validationError(
        `${path}.Type`,
        'one of None, Redirect, ReplaceQuery, AddSuggestion',
        action.Type,
      );
  }
}

/**
 * Strict `SearchResponse` predicate. Lives here (rather than in
 * `search.ts`) so `Models/uiConfig/requestConfig` can reuse it for the
 * merchant `responseHandler` re-validation pass without creating a
 * `requestConfig` ↔ `search` import cycle.
 */
export function getSearchResponseValidationError(value: unknown): ValidationError {
  const objectError = validateObject(value, 'search response');
  if (objectError !== undefined) {
    return objectError;
  }

  const response = value as Record<string, unknown>;
  return (
    validateArray(response.Items, 'Items', validateGenericItem) ??
    validateArray(response.Actions, 'Actions', validateAction) ??
    validateArray(response.Facets, 'Facets', validateFacetCategory) ??
    validateField(response, '', 'Query', 'string', isString) ??
    validateField(response, '', 'OriginalQuery', 'string', isString) ??
    validateField(response, '', 'TotalHits', 'number', isNumber) ??
    validateArray(response.Messages, 'Messages', (message, messagePath) =>
      isString(message) ? undefined : validationError(messagePath, 'string', message),
    ) ??
    validateArray(response.Corrections, 'Corrections', validateCorrection) ??
    validateGenericItem(response.Extra, 'Extra')
  );
}

export function isSearchResponseShape(value: unknown): value is ServerModel.SearchResponse {
  return getSearchResponseValidationError(value) === undefined;
}

export function isFacetsResponseShape(value: unknown): value is ServerModel.FacetsResponse {
  return isObjectRecord(value) && isArrayOf(value.Facets, isFacetCategoryShape);
}
